import Raven from 'raven-js';
import I18n from 'I18n';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import { getFullUrl } from 'hubspot-url-utils';
import { jsx as _jsx } from "react/jsx-runtime";
const sentryLogger = sentryData => {
  const {
    errorType,
    message = '',
    status,
    prefixKey,
    messageType,
    missingKeys
  } = sentryData;
  if (errorType) {
    Raven.captureMessage('Translation Missing', {
      extra: {
        errorType,
        errorMessage: message,
        status,
        prefixKey,
        messageType,
        missingKeys
      }
    });
  }
};
const getComponentToRender = (key, count, errorTokens, additionalTranslationOptions) => {
  const options = Object.assign({}, additionalTranslationOptions, errorTokens, {
    count,
    hubspotHomeUrl: getFullUrl('app')
  });
  const hasHTML = /<("[^"]*"|'[^']*'|[^'">])*>/.test(I18n.text(key, options));
  const ComponentToRender = hasHTML ? FormattedHTMLMessage : FormattedMessage;
  return /*#__PURE__*/_jsx(ComponentToRender, {
    message: key,
    options: options
  });
};
const getErrorKey = translationKeys => translationKeys.find(key => isValidI18nKey(key));
const getErrorTypeLevelKeys = (prefixKey, errorType, action, messageType) => {
  const keys = [];
  if (errorType) {
    if (action) {
      keys.push(`${prefixKey}.${errorType}.${action}.${messageType}`);
    }
    keys.push(`${prefixKey}.${errorType}.default.${messageType}`);
  }
  return keys;
};
const getActionLevelKeys = (prefixKey, action, status, messageType) => {
  const keys = [];
  if (action) {
    if (status) {
      keys.push(`${prefixKey}.default-error.${action}.${status}.${messageType}`);
    }
    keys.push(`${prefixKey}.default-error.${action}.default.${messageType}`);
  }
  return keys;
};
const getDefaultLevelKeys = (prefixKey, status, messageType) => {
  const keys = [`${prefixKey}.default-error.default-action.default.${messageType}`];
  return status ? [`${prefixKey}.default-error.default-action.${status}.${messageType}`, ...keys] : keys;
};
const getLibraryLevelKeys = (status, messageType) => {
  const keys = [`ErrorMessaging.errors.default.${messageType}`];
  return status ? [`ErrorMessaging.errors.${status}.${messageType}`, ...keys] : keys;
};
export const getTranslatedErrorText = (data, messageType, logSentry = false) => {
  const {
    action,
    additionalTranslationOptions = {},
    count = 1,
    prefixKey,
    response
  } = data;
  const {
    status,
    responseJSON = {}
  } = response;
  const {
    errorType,
    errorTokens = {},
    message
  } = responseJSON;
  let missingKeys = [];
  const sentryData = {
    action,
    errorType,
    message,
    status,
    prefixKey,
    messageType
  };
  let translationKeys = getErrorTypeLevelKeys(prefixKey, errorType, action, messageType);
  let langKey = getErrorKey(translationKeys);
  if (!langKey) {
    missingKeys = [...missingKeys, ...translationKeys];
    translationKeys = getActionLevelKeys(prefixKey, action, status, messageType);
    langKey = getErrorKey(translationKeys);
    if (!langKey) {
      missingKeys = [...missingKeys, ...translationKeys];
      translationKeys = getDefaultLevelKeys(prefixKey, status, messageType);
      langKey = getErrorKey(translationKeys);
      if (!langKey) {
        missingKeys = [...missingKeys, ...translationKeys];
        translationKeys = getLibraryLevelKeys(status, messageType);
        langKey = getErrorKey(translationKeys);
        if (!langKey) {
          missingKeys = [...missingKeys, ...translationKeys];
          if (logSentry) {
            sentryLogger(Object.assign({}, sentryData, {
              missingKeys
            }));
          }
          return message;
        }
      }
    }
  }
  if (logSentry && missingKeys.length > 0) {
    sentryLogger(Object.assign({}, sentryData, {
      missingKeys
    }));
  }
  return getComponentToRender(langKey, count, errorTokens, additionalTranslationOptions);
};
export const getTranslatedErrorTitle = (data, logSentry) => getTranslatedErrorText(data, 'title', logSentry);
export const getTranslatedErrorMessage = (data, logSentry) => getTranslatedErrorText(data, 'message', logSentry);
export const configureGetErrorTitle = ({
  logSentry
}) => data => getTranslatedErrorTitle(data, logSentry);
export const configureGetErrorMessage = ({
  logSentry
}) => data => getTranslatedErrorMessage(data, logSentry);